import {
  MultiSelect,
  type Option,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@appretio/ui-kit";
import { useMemo } from "react";
import { ContextField, contextOptions } from "./contextConfig";
import { disabledWrap } from "./ContextDropdown.css";

interface ContextDropdownProps {
  /**
   * Which context field this dropdown is for
   */
  field: ContextField;
  /**
   * List of available options for this context field
   */
  options: string[];
  /**
   * List of filtered options for this context field
   */
  filteredOptions: string[];
  /**
   * Loading state
   */
  isLoading: boolean;
  /**
   * Currently selected options for this context field
   */
  selected: string[];
  /**
   * Sets selected options for this context field
   */
  setSelected: (field: ContextField, value: string[]) => void;
  /**
   * Disables the dropdown trigger.
   */
  disabled?: boolean;
  /**
   * Why the dropdown is disabled, shown as a tooltip on hover and keyboard
   * focus. Ignored when the dropdown is enabled.
   */
  disabledReason?: string;
  /**
   * Highlights the trigger as invalid — set when an action was blocked because
   * this field has no selection.
   */
  error?: boolean;
  /**
   * Render the dropdown as a modal popover — required when the selector lives
   * inside a modal Dialog, otherwise the dialog's scroll lock swallows wheel
   * events on the option list.
   */
  modalPopover?: boolean;
}

export function ContextDropdown({
  field,
  options,
  filteredOptions,
  isLoading,
  selected,
  setSelected,
  disabled,
  disabledReason,
  error,
  modalPopover
}: ContextDropdownProps) {
  const opts: Option[] = useMemo(() => {
    // If no filtered options, return all as relevant
    if (filteredOptions.length === 0) {
      return options.map((item) => ({
        label: item,
        value: item,
        relevant: true
      }));
    }

    // Currently selected values are always relevant — no need to wait for
    // the API to confirm what the user just picked.
    const selectedSet = new Set(selected);
    const filteredSet = new Set(filteredOptions);

    return [
      ...filteredOptions.map((item) => ({
        label: item,
        value: item,
        relevant: true
      })),
      ...options
        .filter((item) => !filteredSet.has(item))
        .map((item) => ({
          label: item,
          value: item,
          relevant: selectedSet.has(item)
        }))
    ];
  }, [options, filteredOptions, selected]);

  const select = (
    <MultiSelect
      loading={isLoading}
      label={contextOptions[field].displayName}
      options={opts}
      value={selected}
      onChange={(value: string[]) => {
        setSelected(field, value);
      }}
      typeToSearch
      disabled={disabled}
      error={error}
      modalPopover={modalPopover}
    />
  );

  if (!disabled || !disabledReason) return select;
  // A disabled trigger takes no focus and no pointer, so the wrapper carries the
  // tooltip and the tab stop.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className={disabledWrap}>
          {select}
        </span>
      </TooltipTrigger>
      <TooltipContent>{disabledReason}</TooltipContent>
    </Tooltip>
  );
}
